import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, Menu, PanelLeft, Search } from "lucide-react";

import { DinnoxBot } from "@/components/guide/dinnox-bot";
import { useDinnoxHidden } from "@/components/guide/use-dinnox-visibility";
import { NotificationBell } from "@/components/shell/notification-bell";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { logout } from "@/lib/auth";
import { useGuide } from "@/lib/guide";
import type { Employee, User } from "@/lib/types";

interface TopBarProps {
  /** Open the mobile nav drawer. Hidden on lg+ where the sidebar is always visible. */
  onMenuOpen?: () => void;
  /** Open the global command palette (also bound to Cmd/Ctrl-K). */
  onOpenSearch?: () => void;
  /** Desktop rail collapse state + toggle (icon-only compact mode). */
  railCompact?: boolean;
  onToggleRail?: () => void;
}

export function TopBar({
  onMenuOpen,
  onOpenSearch,
  railCompact,
  onToggleRail,
}: TopBarProps) {
  const navigate = useNavigate();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<User>("/accounts/users/me/"),
  });
  // The signed-in user's linked employee carries the profile photo (the
  // /accounts/users/me/ payload doesn't). 404s when the user has no
  // employee — fall back to initials, don't retry.
  const myEmployee = useQuery({
    queryKey: ["employees", "me"],
    queryFn: () => api.get<Employee>("/employees/employees/me/"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const displayName = me.data
    ? `${me.data.first_name} ${me.data.last_name}`.trim() || me.data.username
    : "";

  // Dinnobot app-wide show/hide. The toggle only appears when Dinnobot is actually
  // configured for this deployment (otherwise there's nothing to toggle).
  const guide = useGuide();
  const dinnoxAvailable = guide.data?.available ?? false;
  const { hidden: dinnoxHidden, toggle: toggleDinnox } = useDinnoxHidden();

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <header
      className="topbar sticky top-0 z-10 flex h-topbar items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6"
      role="banner"
    >
      {/* Hamburger — only on mobile, opens drawer */}
      {onMenuOpen && (
        <button
          type="button"
          onClick={onMenuOpen}
          aria-label="Open navigation"
          className="inline-flex h-9 w-9 items-center justify-center rounded-btn text-slate hover:bg-slate-soft hover:text-navy lg:hidden"
        >
          <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
        </button>
      )}

      {/* Rail collapse toggle — desktop only */}
      {onToggleRail && (
        <button
          type="button"
          onClick={onToggleRail}
          aria-label={railCompact ? "Expand sidebar" : "Collapse sidebar"}
          aria-pressed={railCompact}
          className="hidden h-9 w-9 items-center justify-center rounded-btn text-slate hover:bg-slate-soft hover:text-navy lg:inline-flex"
        >
          <PanelLeft size={19} strokeWidth={1.5} aria-hidden="true" />
        </button>
      )}

      <div className="relative max-w-md flex-1">
        <button
          type="button"
          onClick={onOpenSearch}
          className="flex h-9 w-full items-center gap-2 rounded-btn border border-slate-200 bg-bg pl-3 pr-2 text-sm text-slate-400 transition-colors hover:border-slate-300 hover:bg-white focus:border-blue focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/20"
          aria-label="Search (Ctrl+K)"
          aria-keyshortcuts="Control+K Meta+K"
        >
          <Search size={16} strokeWidth={1.5} className="shrink-0 text-slate" aria-hidden="true" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="hidden items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-400 sm:inline-flex">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1">
        {dinnoxAvailable && (
          <button
            type="button"
            onClick={toggleDinnox}
            aria-label={dinnoxHidden ? "Show the Dinnobot assistant" : "Hide the Dinnobot assistant"}
            aria-pressed={!dinnoxHidden}
            title={dinnoxHidden ? "Show Dinnobot" : "Hide Dinnobot"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-btn text-slate hover:bg-slate-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/20"
          >
            <span className="grid size-6 place-items-center overflow-hidden rounded-full bg-blue/10">
              <DinnoxBot
                variant="avatar"
                className={
                  dinnoxHidden ? "size-full opacity-40 grayscale" : "size-full"
                }
              />
            </span>
          </button>
        )}

        <NotificationBell />

        <div className="ml-2 flex items-center gap-3 border-l border-slate-200 pl-3">
          <Link
            to="/settings/profile"
            className="flex items-center gap-2.5 rounded-btn px-1 py-1 transition-colors hover:bg-slate-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/20"
            aria-label="Your profile settings"
          >
            <Avatar
              name={displayName}
              photo={myEmployee.data?.photo}
              fallback="U"
              size={32}
              tone="blue"
              previewable={false}
            />
            <div className="hidden leading-tight md:block">
              <p className="text-sm font-medium text-navy">
                {displayName || "—"}
              </p>
              <p className="text-[11px] text-slate">{me.data?.email ?? ""}</p>
            </div>
          </Link>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            aria-label="Sign out"
            className="text-slate hover:text-navy"
          >
            <LogOut size={16} strokeWidth={1.5} aria-hidden="true" />
            <span className="hidden md:inline">Sign out</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
